import React from 'react';
import Select from 'react-select';
import AvailabilityPlannerService from '../../../Services/AvailabilityPlannerService';
import OfficeService from '../../../Services/OfficeService';
import AuthService from '../../../Services/authService';
import ErrorMessage from '../../../Elements/ErrorMessage';
import Loading from '../../../Elements/loading';
import FaIcon from '../../../Elements/FaIcon';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import { LOCATION, ABSENCE } from './availabilityTaxonomy';

// Flag a day when at least this fraction of the selected people are away (on any absence).
const AWAY_ALERT_FRACTION = 0.3;

// Coverage view: per-day count of who is away across the caller's team or an office selection, for a month.
// Reuses the same month data as the other views (GetMonth / GetOfficeMonth); the aggregation is client-side.
export default class AvailabilityPlannerCoverage extends React.Component {
  constructor(props) {
    super(props);
    const loggedInUser = AuthService.getInstance().GetLoggedInUser();
    const defaultOffice = (loggedInUser && loggedInUser.officeId) || 0;
    this.state = {
      baseUserId: loggedInUser ? loggedInUser.userId : 0,
      scope: 'team', // 'team' | 'office'
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      offices: [],
      officeIds: defaultOffice ? [defaultOffice] : [],
      selectedDepartments: [],
      users: [],
      loading: true,
    };
  }

  componentDidMount() {
    OfficeService.GetOfficesForSelect()
      .then((offices) => this.setState({ offices: Array.isArray(offices) ? offices : [] }))
      .catch(() => ErrorMessage.SetErrorMessage('APC28'));
    this.load();
  }

  pad(n) {
    return String(n).padStart(2, '0');
  }

  load() {
    this.setState({ loading: true });
    const officeScope = this.state.scope === 'office';
    const request = officeScope
      ? AvailabilityPlannerService.GetOfficeMonth(this.state.officeIds, this.state.year, this.state.month)
      : AvailabilityPlannerService.GetMonth(this.state.baseUserId, this.state.year, this.state.month);
    request
      .then((data) => this.setState({ users: Array.isArray(data) ? data : [] }))
      .catch(() => ErrorMessage.SetErrorMessage('APC27'))
      .finally(() => this.setState({ loading: false }));
  }

  changeMonth(delta) {
    let month = this.state.month + delta;
    let year = this.state.year;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }
    this.setState({ month, year }, () => this.load());
  }

  setScope(scope) {
    if (scope !== this.state.scope) {
      this.setState({ scope }, () => this.load());
    }
  }

  changeOffices(officeIds) {
    this.setState({ officeIds: Array.isArray(officeIds) ? officeIds : [] }, () => this.load());
  }

  officeOptions() {
    return this.state.offices.map((o) => ({ value: o.officeId, label: o.name }));
  }

  departmentOptions() {
    const set = new Set();
    this.state.users.forEach((u) => set.add(u.departmentName || 'Unassigned'));
    return [...set].sort((a, b) => a.localeCompare(b)).map((d) => ({ value: d, label: d }));
  }

  filteredUsers() {
    if (this.state.scope === 'office' && this.state.selectedDepartments.length > 0) {
      const selected = new Set(this.state.selectedDepartments);
      return this.state.users.filter((u) => selected.has(u.departmentName || 'Unassigned'));
    }
    return this.state.users;
  }

  // People with any absence entry on the given day.
  awayUsersOn(users, dayStr) {
    return users.filter((u) =>
      (u.dayPlans || []).some((p) => p.day && p.day.substring(0, 10) === dayStr && Number(p.absenceHours) > 0)
    );
  }

  // People with the given absence type on the given day.
  absenceUsersOn(users, dayStr, absenceType) {
    return users.filter((u) =>
      (u.dayPlans || []).some(
        (p) => p.day && p.day.substring(0, 10) === dayStr && p.absenceType === absenceType && Number(p.absenceHours) > 0
      )
    );
  }

  // People working (any location) on the given day.
  workingUsersOn(users, dayStr) {
    return users.filter((u) =>
      (u.dayPlans || []).some(
        (p) => p.day && p.day.substring(0, 10) === dayStr && p.location && Number(p.workingHours) > 0
      )
    );
  }

  // People working at the given location (office/home/businesstrip) on the given day.
  workUsersOn(users, dayStr, locationKey) {
    return users.filter((u) =>
      (u.dayPlans || []).some(
        (p) => p.day && p.day.substring(0, 10) === dayStr && p.location === locationKey && Number(p.workingHours) > 0
      )
    );
  }

  namesTitle(label, list) {
    return list.length ? `${label}: ${list.map((u) => u.name).join(', ')}` : '';
  }

  renderControls() {
    const officeScope = this.state.scope === 'office';
    const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;
    const officeOptions = this.officeOptions();
    const officeValue = officeOptions.filter((o) => this.state.officeIds.includes(o.value));
    const deptOptions = this.departmentOptions();
    const deptValue = deptOptions.filter((d) => this.state.selectedDepartments.includes(d.value));
    return (
      <>
        <div className='row p-1 align-items-center'>
          <div className='col-auto'>
            <div
              className='btn-group btn-group-sm'
              role='group'
            >
              <button
                type='button'
                className={`btn ${this.state.scope === 'team' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.setScope('team')}
              >
                My team
              </button>
              <button
                type='button'
                className={`btn ${officeScope ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.setScope('office')}
              >
                Office-wide
              </button>
            </div>
          </div>
          <div className='col-auto d-flex align-items-center h6 mb-0'>
            <FaIcon
              iconName='fa fa-chevron-left'
              tooltipText='Previous month'
              onClick={() => this.changeMonth(-1)}
            />
            <span className='mx-3'>
              {dateTimeHelper.MonthName(this.state.month - 1)} {this.state.year}
            </span>
            <FaIcon
              iconName='fa fa-chevron-right'
              tooltipText='Next month'
              onClick={() => this.changeMonth(1)}
            />
          </div>
        </div>
        {officeScope && (
          <div className='row p-1'>
            <div className='col-md-6'>
              <div className='ap-filter-label'>Offices (none = all)</div>
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={officeOptions}
                value={officeValue}
                onChange={(sel) => this.changeOffices((sel || []).map((o) => o.value))}
                placeholder='All offices'
                menuPortalTarget={menuPortalTarget}
                styles={selectStyles}
              />
            </div>
            <div className='col-md-6'>
              <div className='ap-filter-label'>Departments (none = all)</div>
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={deptOptions}
                value={deptValue}
                onChange={(sel) => this.setState({ selectedDepartments: (sel || []).map((d) => d.value) })}
                placeholder='All departments'
                menuPortalTarget={menuPortalTarget}
                styles={selectStyles}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  isToday(dayNumber) {
    const today = new Date();
    return (
      today.getFullYear() === this.state.year &&
      today.getMonth() === this.state.month - 1 &&
      today.getDate() === dayNumber
    );
  }

  renderTable() {
    const users = this.filteredUsers();
    const total = users.length;
    if (total === 0) {
      return <div className='ap-empty p-3'>No people in this selection.</div>;
    }
    const lastDay = dateTimeHelper.LastDayOfMonth(this.state.year, this.state.month - 1);
    // Per-day aggregation (days run across the columns, like the My team / Team grid calendars).
    const days = [];
    for (let day = 1; day <= lastDay; day++) {
      const dayStr = `${this.state.year}-${this.pad(this.state.month)}-${this.pad(day)}`;
      const away = this.awayUsersOn(users, dayStr);
      const working = this.workingUsersOn(users, dayStr);
      const fraction = total > 0 ? away.length / total : 0;
      const isWeekend = dateTimeHelper.IsWeekend(this.state.year, this.state.month - 1, day);
      const byAbsence = {};
      Object.keys(ABSENCE).forEach((key) => {
        byAbsence[key] = this.absenceUsersOn(users, dayStr, key);
      });
      const byLocation = {};
      Object.keys(LOCATION).forEach((key) => {
        byLocation[key] = this.workUsersOn(users, dayStr, key);
      });
      days.push({
        day,
        isWeekend,
        away,
        working,
        byAbsence,
        byLocation,
        alert: !isWeekend && away.length > 0 && fraction >= AWAY_ALERT_FRACTION,
      });
    }
    const cellClass = (d) => {
      let c = 'ap-cov-cell';
      if (d.isWeekend) {
        c += ' ap-cov-weekend';
      } else if (this.isToday(d.day)) {
        c += ' ap-cov-today';
      }
      return c;
    };
    return (
      <div className='ap-calendar'>
        <table className='ap-table ap-cov-table'>
          <thead>
            <tr>
              <th className='ap-th-name'>Coverage ({total})</th>
              {days.map((d) => (
                <th
                  key={d.day}
                  className={d.isWeekend ? 'ap-th-weekend' : ''}
                >
                  {this.isToday(d.day) ? <span className='ap-th-today-badge'>{d.day}</span> : d.day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className='ap-cov-total-row ap-cov-total-top'>
              <td className='ap-name'>Total (Absences)</td>
              {days.map((d) => (
                <td
                  key={d.day}
                  className={`${cellClass(d)} ap-cov-abs-cell${d.alert ? ' ap-cov-alert-cell' : ''}`}
                  title={this.namesTitle('Away', d.away)}
                >
                  {d.away.length || ''}
                </td>
              ))}
            </tr>
            <tr className='ap-cov-total-row'>
              <td className='ap-name'>Total (Work)</td>
              {days.map((d) => (
                <td
                  key={d.day}
                  className={cellClass(d)}
                  title={this.namesTitle('Working', d.working)}
                >
                  {d.working.length || ''}
                </td>
              ))}
            </tr>
            <tr className='ap-cov-spacer'>
              <td colSpan={days.length + 1} />
            </tr>
            {Object.entries(ABSENCE).map(([key, meta]) => (
              <tr key={`abs-${key}`}>
                <td className='ap-name'>
                  <i className={`fa-solid ${meta.icon} me-1`} />
                  {meta.label}
                </td>
                {days.map((d) => (
                  <td
                    key={d.day}
                    className={`${cellClass(d)} ap-cov-abs-cell`}
                    title={this.namesTitle(meta.label, d.byAbsence[key])}
                  >
                    {d.byAbsence[key].length || ''}
                  </td>
                ))}
              </tr>
            ))}
            {Object.entries(LOCATION).map(([key, meta]) => (
              <tr key={`loc-${key}`}>
                <td className='ap-name'>
                  <i className={`fa-solid ${meta.icon} me-1`} />
                  {meta.label}
                </td>
                {days.map((d) => (
                  <td
                    key={d.day}
                    className={cellClass(d)}
                    title={this.namesTitle(meta.label, d.byLocation[key])}
                  >
                    {d.byLocation[key].length || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className='text-muted small px-1 mt-1'>
          The two totals sum their detail rows: "Total (Absences)" = people on any absence (broken down by type), "Total
          (Work)" = people working (broken down by location). A day is highlighted when at least{' '}
          {Math.round(AWAY_ALERT_FRACTION * 100)}% are away (weekends excluded). Hover a cell to see the people.
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.renderControls()}
        {this.state.loading ? <Loading /> : this.renderTable()}
      </div>
    );
  }
}
